import dayjs from 'dayjs';
import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useState, useMemo, useCallback } from 'react';
import { useForm, Controller, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Checkbox, CheckedState } from '@ge/components/checkbox';
import { Loader } from '@ge/components/loader';
import { Dialog } from '@ge/components/modal';
import { useNotification } from '@ge/components/notification';
import { Select } from '@ge/components/select';
import NotificationMessage from '@ge/feat-monitor/components/notification-message';
import useStateRef from '@ge/hooks/state-ref';
import {
  CaseTypeMapping,
  EntityType,
  MonitorCaseStateMapping,
  NewMonitorCaseKeys,
  NotificationType,
  MonitorManualCaseMapping,
} from '@ge/models/constants';
import { DateTimeMetaField } from '@ge/shared/components/meta-fields';
import {
  useScheduleMonitorCase,
  useSiteAndAssetLables,
  useStorageInverters,
} from '@ge/shared/data-hooks';
import { getUserProfileInfo } from '@ge/shared/services/auth';
import { toSiteISOString } from '@ge/shared/util';

const LoaderContainer = styled.div`
  position: relative;
  min-width: 520px;
  min-height: 200px;
`;

const CaseDialogSegment = styled.div`
  margin-top: 20px;
  padding-bottom: 10px;
  &:first-of-type {
    margin-top: 0;
  }
  dl {
    display: grid;
    grid-auto-columns: 1fr;
    grid-auto-flow: row;
    margin: 0;
    width: 250px;
  }
  dt {
    color: ${(props) => props.theme.entityDetails.notes.caseInfo};
    font: 300 11px/15px;
    grid-row: 1;
  }
  dd {
    font: 300 13px/15px;
    margin-left: 0;
  }
`;


const AddCaseFormRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
`;

const MonitorCaseFormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  margin-bottom: 15px;
  &.local-time-row {
    justify-content: flex-end;
  }
  &.date-time-meta-field {
    margin-top: 15px;
  }
`;

const LocalTimeToggle = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledCheckbox = styled(Checkbox)`
  align-items: center;
  font: 12px;
  line-height: 14px;
  font-weight: 300;
`;

const AddCaseFooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const FooterButtons = styled.div`
  margin-left: auto;

  button:not(:last-of-type) {
    margin-right: 20px;
  }
`;

const CaseFooter = ({ onClick, onSubmit, isLoading, isFormValid }) => {
  const { t } = useTranslation(['entity-details']);
  return (
    <AddCaseFooterWrapper>
      <FooterButtons>
        <Button onClick={onClick} disabled={isLoading}>
          {t('general:cancel', 'Cancel')}
        </Button>
        <Button primary onClick={onSubmit} disabled={isLoading || !isFormValid}>
          {t('general:save', 'Save')}
        </Button>
      </FooterButtons>
    </AddCaseFooterWrapper>
  );
};

CaseFooter.propTypes = {
  isLoading: PropTypes.bool,
  onClick: PropTypes.func,
  onSubmit: PropTypes.func,
  isFormValid: PropTypes.bool.isRequired,
};

export const NewMonitorCaseDialog = ({ isOpen, onClose, setCaseOptions, caseOptions }) => {
  const { t, ready } = useTranslation(['entity-details'], { useSuspense: false });
  const { notify } = useNotification();

  const [caseLevelOptions] = useState([
    { value: EntityType.SITE, label: NewMonitorCaseKeys.SITE_LABEL },
    { value: EntityType.ASSET, label: NewMonitorCaseKeys.ASSET_LABLE },
  ]);
  const [alarmCodeOptions] = useState([
    { value: NewMonitorCaseKeys.S510, label: NewMonitorCaseKeys.AlARM_CODE_S510_LABEL },
    { value: NewMonitorCaseKeys.S511, label: NewMonitorCaseKeys.ALARM_CODE_S511_LABEL },
  ]);

  const [localTime, setLocalTime, localTimeRef] = useStateRef(CheckedState.CHECKED);
  const [startTimeInteracted, setStartTimeInteracted] = useState(false);
  const { isLoading: inverterAssetsLoading } = useStorageInverters({
    enabled: true,
    inverterType: 'solar',
  });
  const { scheduleMonitorCaseApi, isLoading } = useScheduleMonitorCase();
  const userName = getUserProfileInfo();

  const getAssetById = useStoreState((store) => store.assets.getAssetById);
  const { getSiteById } = useStoreState((store) => store.sites);
  const methods = useForm({ mode: 'onChange' });
  const { control } = methods;
  const selectedSiteOption = useWatch({ control, name: NewMonitorCaseKeys.SELECT_SITES });
  const selectedAssetOption = useWatch({ control, name: NewMonitorCaseKeys.SELECT_ASSETS });
  const alarmCodeState = useWatch({ control, name: NewMonitorCaseKeys.ALARM_CODE });
  const manualCaseState = useWatch({ control, name: NewMonitorCaseKeys.MANUAL_CASE });
  const selectedSiteId = selectedSiteOption?.value;

  const isAssetCaseLevel = caseOptions?.value === EntityType.ASSET;

  const { filteredSites, filteredAssets, isLoading: isEntityLoading } = useSiteAndAssetLables(
    caseOptions?.value,
    selectedSiteId,
  );

  const combinedLoading = isLoading || isEntityLoading || inverterAssetsLoading;

  const handleStartTimeInteraction = useCallback(() => {
    if (!startTimeInteracted) {
      setStartTimeInteracted(true);
    }
  }, [startTimeInteracted]);

  const isFormValid = useMemo(() => {
    const siteSelected = !!control.getValues(NewMonitorCaseKeys.SELECT_SITES);
    const alarmCodeSelected = !!control.getValues(NewMonitorCaseKeys.ALARM_CODE);
    const startTimeSet = !!control.getValues(NewMonitorCaseKeys.START_TIME) && startTimeInteracted;
    const caseTypeSelected = !!control.getValues(NewMonitorCaseKeys.MANUAL_CASE);

    const baseState = siteSelected && alarmCodeSelected && startTimeSet && caseTypeSelected;

    const assetSelected = !!control.getValues(NewMonitorCaseKeys.SELECT_ASSETS);
    return isAssetCaseLevel ? baseState && assetSelected : baseState;
  }, [
    control,
    isAssetCaseLevel,
    selectedSiteOption?.value,
    selectedAssetOption?.value,
    alarmCodeState?.value,
    startTimeInteracted,
  ]);

  const siteTimezone = useMemo(() => {
    if (!selectedSiteId) return '';
    const site = getSiteById(selectedSiteId);
    return site?.timezone || '';
  }, [selectedSiteId, getSiteById]);

  const toggleLocalTime = useCallback(() => {
    setLocalTime(
      localTimeRef.current === CheckedState.CHECKED ? CheckedState.UNCHECKED : CheckedState.CHECKED,
    );
  }, [setLocalTime, localTimeRef]);

  const onSubmit = useCallback(
    async (values) => {
      const { select_sites, select_assets, start_time, alarm_code } = values;

      let entityIds;
      let siteId;

      if (caseOptions?.value === EntityType.SITE) {
        entityIds = [select_sites?.value].filter(Boolean);
        siteId = select_sites?.value;
      } else {
        entityIds = [select_assets?.value].filter(Boolean);
        siteId = select_sites?.value;
      }

      if (entityIds.length === 0) {
        console.error('No entity selected for case creation.');
        methods.trigger();
        return;
      }

      let snoozeTime;
      if (start_time) {
        if (localTime === CheckedState.UNCHECKED && siteTimezone) {
          snoozeTime = dayjs(toSiteISOString(start_time, siteTimezone)).valueOf();
        } else {
          snoozeTime = dayjs(start_time).valueOf();
        }
      }
      const casesToBeLoaded = entityIds.map((id) => ({
        assetId: caseOptions?.value === EntityType.ASSET ? id : '',
        siteId: caseOptions?.value === EntityType.SITE ? id : siteId,
        alarmTime: dayjs().valueOf(),
        snoozeTime,
        state: String(MonitorCaseStateMapping.SNOOZED),
        type: String(CaseTypeMapping.MANUAL),
        caseLevel: manualCaseState?.value,
        assetType: isAssetCaseLevel
          ? MonitorManualCaseMapping[getAssetById(id)?.type]
          : MonitorManualCaseMapping[getSiteById(id)?.type[0]],
        alarmInitial: alarm_code.value,
        responsibleAlarm: alarm_code.value,
        createdBy: userName?.preferred_username || userName?.given_name,
      }));

      try {
        const result = await scheduleMonitorCaseApi(casesToBeLoaded);
        if (result && result.length > 0) {
          onClose(casesToBeLoaded);
        } else {
          const ErrorMessage = (
            <NotificationMessage
              message={t(
                'case_scheduling_failed',
                'Case Scheduling Failed Due To In-Proper Response',
              ).toUpperCase()}
              type={NotificationType.ERROR}
            />
          );
          notify({
            message: ErrorMessage,
            type: NotificationType.ERROR,
          });
        }
      } catch (error) {
        methods.trigger();
        const errorMessage = (
          <NotificationMessage
            message={t('case_scheduling_failed', 'Case Scheduling Failed').toUpperCase()}
            type={NotificationType.ERROR}
          />
        );

        notify({
          message: errorMessage,
          type: NotificationType.ERROR,
        });
      }
    },
    [
      localTime,
      siteTimezone,
      scheduleMonitorCaseApi,
      onClose,
      methods,
      caseOptions?.value,
      getAssetById,
      getSiteById,
      isAssetCaseLevel,
      manualCaseState?.value,
      userName?.preferred_username,
      userName?.given_name,
      notify,
      t,
    ],
  );

  const handleCaseLevelChange = (onChange, val) => {
    onChange(val);
    setCaseOptions(val);
    methods.setValue(NewMonitorCaseKeys.SELECT_SITES, null);
    methods.setValue(NewMonitorCaseKeys.SELECT_ASSETS, null);
    methods.setValue(NewMonitorCaseKeys.ALARM_CODE, null);
    methods.setValue(NewMonitorCaseKeys.START_TIME, null);
    setStartTimeInteracted(false);
    setLocalTime(CheckedState.CHECKED);
  };

  if (!ready) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={() => null}
      header={t('case.schedule_manual_case', 'Schedule Manual Case')}
      footer={
        <CaseFooter
          isLoading={combinedLoading}
          onClick={onClose}
          onSubmit={methods.handleSubmit(onSubmit)}
          isFormValid={isFormValid}
        />
      }
      contentWidth
    >
      <FormProvider {...methods}>
        {combinedLoading ? (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        ) : (
          <>
            <CaseDialogSegment>
              <Controller
                name={NewMonitorCaseKeys.MANUAL_CASE}
                rules={{ required: true }}
                render={({ onChange, value }) => (
                  <Select
                    id="manual-case-select"
                    label={`${t('schedule_monitor_case.manual_case_type', 'Manual Case Type')}*`}
                    onChange={(val) => handleCaseLevelChange(onChange, val)}
                    value={value}
                    options={caseLevelOptions}
                    name={NewMonitorCaseKeys.MANUAL_CASE}
                    maxWidth={257}
                    minWidth={257}
                    placeholder={'Select Manual Case Type'}
                  />
                )}
              />
            </CaseDialogSegment>
            <CaseDialogSegment>
              <AddCaseFormRow>
                <Controller
                  name={NewMonitorCaseKeys.SELECT_SITES}
                  rules={{ required: true }}
                  defaultValue={null}
                  render={({ onChange, value }) => (
                    <Select
                      id="case-select-sites"
                      label={`${t('schedule_monitor_case.select_site', 'Select Site')}*`}
                      onChange={(val) => {
                        onChange(val);
                        methods.setValue(NewMonitorCaseKeys.SELECT_ASSETS, null);
                      }}
                      value={value}
                      options={filteredSites}
                      name={NewMonitorCaseKeys.SELECT_SITES}
                      maxWidth={257}
                      minWidth={257}
                      placeholder={'Select Site'}
                      disabled={!caseOptions?.value}
                      searchable={true}
                      scrollbarStyles={true} 
                    />
                  )}
                />
                <Controller
                  name={NewMonitorCaseKeys.SELECT_ASSETS}
                  rules={{ required: isAssetCaseLevel }}
                  defaultValue={null}
                  render={({ onChange, value }) => (
                    <Select
                      id="case-select-assets"
                      label={`${t('schedule_monitor_case.select_assets', 'Select Asset')}*`}
                      onChange={onChange}
                      value={value}
                      options={filteredAssets}
                      name={NewMonitorCaseKeys.SELECT_ASSETS}
                      maxWidth={257}
                      minWidth={257}
                      placeholder={'Select Asset'}
                      disabled={!isAssetCaseLevel || !selectedSiteId}
                      searchable={true}
                      scrollbarStyles={true} 
                    />
                  )}
                />
              </AddCaseFormRow>
            </CaseDialogSegment>
            <CaseDialogSegment>
              <AddCaseFormRow>
                <Controller
                  name={NewMonitorCaseKeys.ALARM_CODE}
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <Select
                      id="case-alarm-code-select"
                      label={`${t('schedule_monitor_case.alarm_code', 'Alarm Code')}*`}
                      onChange={onChange}
                      value={value}
                      options={alarmCodeOptions}
                      name={NewMonitorCaseKeys.ALARM_CODE}
                      disabled={!caseOptions?.value}
                      maxWidth={257}
                      minWidth={257}
                      placeholder={'Select Alarm'}
                    />
                  )}
                />
                <MonitorCaseFormRow className={'date-time-meta-field'}>
                  <DateTimeMetaField
                    name={NewMonitorCaseKeys.START_TIME}
                    label={`${t('schedule_monitor_case.start_time', 'Start Time')}`}
                    defaultSelection="currentDate"
                    metadata={{ required: true, readOnly: !caseOptions?.value }}
                    validators={[{ earliestDate: 'currentDate' }]}
                    timezone={
                      localTime === CheckedState.UNCHECKED && siteTimezone ? siteTimezone : ''
                    }
                    trackUserInteraction={true}
                    onUserInteraction={handleStartTimeInteraction}
                  />
                </MonitorCaseFormRow>
              </AddCaseFormRow>
              <MonitorCaseFormRow className={'local-time-row'}>
                <LocalTimeToggle>
                  <StyledCheckbox
                    label={t('schedule_monitor_case.local_time', 'Local Time')}
                    checkState={localTime}
                    onChange={toggleLocalTime}
                    disabled={!caseOptions?.value}
                  />
                </LocalTimeToggle>
              </MonitorCaseFormRow>
            </CaseDialogSegment>
          </>
        )}
      </FormProvider>
    </Dialog>
  );
};

NewMonitorCaseDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  setCaseOptions: PropTypes.func,
  caseOptions: PropTypes.object,
};
